import {CART_VERSION, CartItemV1, CartV1, MAX_CART_ITEMS, emptyCart} from './types';

export function cartStorageKey(userId: string, orgId: string): string {
  return `lens.cart.v${CART_VERSION}.${userId}.${orgId}`;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Loose on the coordinates, strict on the shape: enough that the value survives a round trip. */
const isGeometryish = (value: unknown): boolean =>
  isRecord(value) && typeof value.type === 'string' && 'coordinates' in value;

const isParseableDate = (value: unknown): boolean =>
  typeof value === 'string' && !Number.isNaN(new Date(value).getTime());

function isValidSnapshot(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.featureName === 'string' &&
    typeof value.projectName === 'string' &&
    typeof value.sourceDisplayName === 'string' &&
    (value.thumbnailUrl === null || typeof value.thumbnailUrl === 'string')
  );
}

/**
 * Drops anything that couldn't be submitted or rendered. A stored item is only useful if it carries
 * every field the checkout endpoint requires, so a partial one is discarded rather than repaired.
 */
function isValidItem(value: unknown): value is CartItemV1 {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.id === 'string' &&
    typeof value.addedAt === 'string' &&
    typeof value.projectId === 'string' &&
    typeof value.featureCollectionId === 'number' &&
    typeof value.featureId === 'number' &&
    typeof value.sourceId === 'string' &&
    // An unparseable date would render as "Invalid date" and still submit.
    isParseableDate(value.sensingTime) &&
    typeof value.centsPerAcre === 'number' &&
    typeof value.billableAcres === 'number' &&
    // Forwarded verbatim to the endpoint, so junk here becomes a 400 at submit.
    (value.geometry === undefined || isGeometryish(value.geometry)) &&
    isValidSnapshot(value.snapshot)
  );
}

/**
 * Rebuilds an item from known fields only. Whole-cart writes persist whatever a read returned, so
 * without this a key dropped from the type would survive every rewrite and keep costing quota.
 */
function normalizeItem(item: CartItemV1): CartItemV1 {
  return {
    id: item.id,
    addedAt: item.addedAt,
    projectId: item.projectId,
    featureCollectionId: item.featureCollectionId,
    featureId: item.featureId,
    sourceId: item.sourceId,
    sensingTime: item.sensingTime,
    centsPerAcre: item.centsPerAcre,
    billableAcres: item.billableAcres,
    ...(item.geometry ? {geometry: item.geometry} : {}),
    snapshot: {
      featureName: item.snapshot.featureName,
      projectName: item.snapshot.projectName,
      sourceDisplayName: item.snapshot.sourceDisplayName,
      thumbnailUrl: item.snapshot.thumbnailUrl,
      ...(item.snapshot.thumbnailIsOverride ? {thumbnailIsOverride: true} : {}),
    },
  };
}

export function readCart(userId: string | null, orgId: string | null): CartV1 {
  if (!userId || !orgId) {
    return emptyCart();
  }
  try {
    const raw = localStorage.getItem(cartStorageKey(userId, orgId));
    if (!raw) {
      return emptyCart();
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.version !== CART_VERSION || !Array.isArray(parsed.items)) {
      return emptyCart();
    }
    return {
      version: CART_VERSION,
      items: parsed.items.filter(isValidItem).map(normalizeItem).slice(0, MAX_CART_ITEMS),
    };
  } catch {
    return emptyCart();
  }
}

export function writeCart(userId: string | null, orgId: string | null, cart: CartV1): void {
  if (!userId || !orgId) {
    return;
  }
  try {
    localStorage.setItem(cartStorageKey(userId, orgId), JSON.stringify(cart));
  } catch {
    // Blocked or full storage: the in-memory cart still works for this tab, and warning on every
    // mutation would be noisier than the lost persistence is worth.
    return;
  }
}

export function clearStoredCart(userId: string | null, orgId: string | null): void {
  if (!userId || !orgId) {
    return;
  }
  try {
    localStorage.removeItem(cartStorageKey(userId, orgId));
  } catch {
    return;
  }
}
